import clsx from 'clsx';
import type { ToolCallState } from '@/hooks/use-streaming-v2';
import { formatNumber } from '@/lib/format';
import { PhaseShell } from './ToolResultRendererSkeleton';
import { Skeleton } from '@/components/ui/Skeleton';

interface ScenarioData {
  name: string;
  changes: Record<string, unknown>;
  outputs: Record<string, unknown>;
}

interface CompareData {
  scenarios: ScenarioData[];
  comparison?: Record<string, unknown>;
}

function isCompareResult(data: unknown): data is CompareData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'scenarios' in data &&
    Array.isArray((data as CompareData).scenarios)
  );
}

function getOutputKeys(scenarios: ScenarioData[]): string[] {
  const keys = new Set<string>();
  for (const s of scenarios) {
    for (const key of Object.keys(s.outputs)) keys.add(key);
  }
  return Array.from(keys);
}

function getBaseScenario(scenarios: ScenarioData[]): ScenarioData | undefined {
  return scenarios.find((s) => s.name.toLowerCase() === 'base') ?? scenarios[0];
}

function renderComplete(result: CompareData) {
  const { scenarios } = result;
  const outputKeys = getOutputKeys(scenarios);
  const baseScenario = getBaseScenario(scenarios);
  return (
    <div className="space-y-2">
      <div className="text-xs font-mono font-semibold text-accent tracking-[1.2px] uppercase">
        Scenario Comparison
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-panel-border">
              <th className="text-left text-xs font-mono font-semibold text-text-muted uppercase tracking-[1px] px-2 py-1">
                Metric
              </th>
              {scenarios.map((s) => (
                <th key={s.name} className="text-right text-xs font-mono font-semibold text-text-muted uppercase tracking-[1px] px-2 py-1">
                  {s.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {outputKeys.map((key) => {
              const baseValue = baseScenario ? baseScenario.outputs[key] : undefined;
              return (
                <tr key={key} className="border-b border-panel-border/50 hover:bg-accent-faint transition-colors">
                  <td className="text-sm font-mono text-text-muted px-2 py-1">{key}</td>
                  {scenarios.map((s) => {
                    const value = s.outputs[key];
                    const numValue = typeof value === 'number' ? value : null;
                    const numBase = typeof baseValue === 'number' ? baseValue : null;
                    const delta = numValue !== null && numBase !== null ? numValue - numBase : null;
                    const isBase = s === baseScenario;
                    return (
                      <td key={s.name} className="text-right px-2 py-1">
                        <div className={clsx(
                          'text-sm font-mono tabular-nums',
                          isBase ? 'text-text-bright' : 'text-text',
                        )}>
                          {numValue !== null ? formatNumber(numValue, 4) : String(value ?? '-')}
                        </div>
                        {!isBase && delta !== null && delta !== 0 && (
                          <div className={clsx(
                            'text-xs font-mono tabular-nums',
                            delta > 0 ? 'text-accent' : 'text-danger',
                          )}>
                            {delta > 0 ? '+' : ''}{formatNumber(delta, 4)}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface ScenarioComparisonProps {
  state: ToolCallState;
}

/**
 * Phase 4 Session 3 — matrix-branch progressive column paint.
 * Each `scenario_computed` event fills its column (ordered by
 * `scenario_index`); un-painted columns render with `(pending)` until
 * `scenarios_complete` flips them to `(aborted)`.
 */
function renderInProgress(state: Extract<ToolCallState, { phase: 'executing' }>) {
  const startedEvent = state.progress.find((p) => p.phase === 'scenarios_started');
  const scenarioEvents = state.progress.filter((p) => p.phase === 'scenario_computed');
  const allTerminated = state.progress.some((p) => p.phase === 'scenarios_complete');
  const startedPayload = startedEvent?.payload as
    | { scenario_count?: number; output_names?: string[] }
    | undefined;
  const totalCount = startedPayload?.scenario_count ?? 0;
  const outputNames = startedPayload?.output_names ?? [];

  const eventByIndex = new Map<number, { scenario_name: string; outputs: Record<string, number> }>();
  for (const evt of scenarioEvents) {
    const payload = evt.payload as { scenario_index?: number; scenario_name: string; outputs: Record<string, number> };
    if (typeof payload.scenario_index === 'number') {
      eventByIndex.set(payload.scenario_index, payload);
    }
  }

  if (totalCount === 0 || outputNames.length === 0) {
    return (
      <PhaseShell variant="in-progress" title="Scenario Comparison">
        <div className="text-xs font-mono text-text-muted">Comparing scenarios…</div>
      </PhaseShell>
    );
  }

  return (
    <PhaseShell variant="in-progress" title="Scenario Comparison">
      <div className="overflow-x-auto" data-testid="scenario-matrix-progress">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-panel-border">
              <th className="text-left text-xs font-mono font-semibold text-text-muted uppercase tracking-[1px] px-2 py-1">
                Metric
              </th>
              {Array.from({ length: totalCount }).map((_, i) => {
                const event = eventByIndex.get(i);
                const status = event ? 'painted' : allTerminated ? 'aborted' : 'pending';
                return (
                  <th
                    key={i}
                    data-col-painted={event ? 'true' : 'false'}
                    data-col-status={status}
                    className={clsx(
                      'text-right text-xs font-mono font-semibold uppercase tracking-[1px] px-2 py-1',
                      event ? 'text-text-muted' : 'text-text-dim',
                    )}
                  >
                    {event ? event.scenario_name : status === 'aborted' ? '(aborted)' : '(pending)'}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {outputNames.map((output) => (
              <tr key={output} className="border-b border-panel-border/50">
                <td className="text-sm font-mono text-text-muted px-2 py-1">{output}</td>
                {Array.from({ length: totalCount }).map((_, i) => {
                  const event = eventByIndex.get(i);
                  if (!event) {
                    return (
                      <td
                        key={i}
                        data-col-painted="false"
                        className="text-right text-sm font-mono text-text-dim px-2 py-1"
                      >
                        —
                      </td>
                    );
                  }
                  return (
                    <td
                      key={i}
                      data-col-painted="true"
                      className="text-right text-sm font-mono text-text px-2 py-1 tabular-nums"
                    >
                      {formatNumber(event.outputs[output] ?? 0, 4)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PhaseShell>
  );
}

/**
 * Phase-aware ScenarioComparison. Per directive §7.5:
 * - skeleton: empty columns labeled with scenario names from `partialInput.scenarios`.
 * - in-progress: column-by-column paint driven by Phase 4 Session 3 progress events.
 * - complete: existing render.
 * - aborted: dim columns + ABORTED badge.
 */
export function ScenarioComparison({ state }: ScenarioComparisonProps) {
  if (state.phase === 'complete' && isCompareResult(state.result)) {
    return renderComplete(state.result);
  }
  if (state.phase === 'complete') {
    return (
      <div className="text-xs font-mono text-text-muted">
        <pre className="whitespace-pre-wrap">{JSON.stringify(state.result, null, 2)}</pre>
      </div>
    );
  }

  if (state.phase === 'executing') {
    return renderInProgress(state);
  }

  const inputData =
    state.phase === 'input_streaming' ? state.partialInput
    : state.phase === 'input_ready' ? state.input
    : state.phase === 'aborted' ? (state.input ?? {})
    : {};
  const probe = inputData as Partial<{ scenarios: Array<{ name?: string }> }>;
  const scenarioNames = (probe.scenarios ?? []).map((s) => s.name ?? '...');
  const variant = state.phase === 'aborted' ? 'aborted' : 'skeleton';

  return (
    <PhaseShell variant={variant} title="Scenario Comparison">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-panel-border">
              <th className="text-left text-xs font-mono font-semibold text-text-muted uppercase tracking-[1px] px-2 py-1">
                Metric
              </th>
              {scenarioNames.length > 0 ? scenarioNames.map((name, i) => (
                <th key={i} className="text-right text-xs font-mono font-semibold text-text-muted uppercase tracking-[1px] px-2 py-1">
                  {name}
                </th>
              )) : (
                <th className="text-right px-2 py-1"><Skeleton className="h-3 w-16 ml-auto" /></th>
              )}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 3 }).map((_, ri) => (
              <tr key={ri} className="border-b border-panel-border/50">
                <td className="px-2 py-1"><Skeleton className="h-3 w-16" /></td>
                {(scenarioNames.length > 0 ? scenarioNames : ['']).map((_, ci) => (
                  <td key={ci} className="text-right px-2 py-1"><Skeleton className="h-3 w-12 ml-auto" /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PhaseShell>
  );
}
